import {
  useShareDeleteMembershipMutation,
  ShareMembershipInfoFragment,
  ShareListItemFragment,
  ShareDeleteMembershipMutation,
} from 'gqlTypes'
import { deleteShareMembershipWithPreview } from 'modules/shares/graphql/cache'
import { useCallback, useState } from 'react'
import { useToast } from 'containers/ToastManager'
import { ErrorHandler } from 'modules/tracing'
import { useModalContext } from 'containers/ModalManager/ModalContext'
import { useIsMountedRef } from 'utils/hooks/useIsMountedRef'

const includesConfirmationRequiredError = (
  data: ShareDeleteMembershipMutation
) =>
  data?.shareDeleteMembership?.errors?.some(
    e => e?.code === 'CONFIRMATION_REQUIRED'
  )

interface UseDeleteMemberProps {
  member: ShareMembershipInfoFragment
  share: Partial<ShareListItemFragment>
}

export const useDeleteMember = ({ share, member }: UseDeleteMemberProps) => {
  const { showToast } = useToast()
  const { hideModal } = useModalContext()
  const [dialogOpen, setDialogOpen] = useState(false)
  const isMountedRef = useIsMountedRef()

  const [
    mutate,
    { loading: deleteMemberLoading },
  ] = useShareDeleteMembershipMutation({
    // TODO: Migrate shareDeleteMembership mutation to use unified error handler
    //       https://github.com/sketch-hq/Cloud/issues/9274
    redirectErrors: false,
    update(cache, { data }) {
      if (!data?.shareDeleteMembership) {
        ErrorHandler.shouldNeverHappen(
          'Share delete member mutation should return valid data'
        )
        return
      }
      if (includesConfirmationRequiredError(data)) return
      deleteShareMembershipWithPreview({
        cache,
        data,
        share,
        member,
      })
      if (
        data.shareDeleteMembership.membership?.share?.userCanUpdateSettings ===
        false
      ) {
        hideModal()
        showToast("You can no longer view this document's settings")
      }
    },
    onError() {
      setDialogOpen(false)
      showToast(
        `Failed to remove '${member.email}'. Please try again.`,
        'negative'
      )
    },
    onCompleted(data) {
      if (includesConfirmationRequiredError(data)) {
        setDialogOpen(true)
      } else {
        isMountedRef && setDialogOpen(false)
      }
    },
  })

  const deleteMember = useCallback(
    (confirmed: boolean = false) =>
      mutate({
        variables: {
          membershipIdentifier: member.identifier,
          confirmed,
        },
      }),
    [member, mutate]
  )

  const getDeleteMemberConfirmDialogProps = useCallback(
    () => ({
      hideModal() {
        setDialogOpen(false)
      },
      onConfirm() {
        deleteMember(true)
      },
      isModalOpen: true,
      confirmButton: {
        text: 'Confirm',
        intent: 'positive',
        loading: deleteMemberLoading,
      } as const,
    }),
    [setDialogOpen, deleteMember, deleteMemberLoading]
  )

  return {
    deleteMember,
    deleteMemberLoading,
    deleteMemberConfirmDialogOpen: dialogOpen,
    getDeleteMemberConfirmDialogProps,
  }
}
